import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Lock, Save } from 'lucide-react';
import { api } from '@/lib/api-client';
import type { SystemSetting, SystemSettingInput } from '@/lib/types';

function SettingRow({ setting }: { setting: SystemSetting }): JSX.Element {
  const queryClient = useQueryClient();
  const [value, setValue] = useState(setting.secret ? '' : setting.value);

  const save = useMutation({
    mutationFn: (v: SystemSettingInput) =>
      api.patch<SystemSetting>(`/api/admin/settings/${encodeURIComponent(setting.key)}`, v),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/settings'] });
    },
  });

  return (
    <tr className="hover:bg-slate-50">
      <td className="px-4 py-2.5 align-top">
        <div className="flex items-center gap-2 font-mono text-xs text-slate-700">
          {setting.secret && <Lock className="h-3 w-3 text-amber-600" />}
          {setting.key}
        </div>
        {setting.description && (
          <p className="mt-1 text-[11px] text-slate-500">{setting.description}</p>
        )}
      </td>
      <td className="px-4 py-2.5 align-top">
        <input
          className="input font-mono text-xs"
          type={setting.secret ? 'password' : 'text'}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={setting.secret ? (setting.value ? '•••••• (beállítva)' : 'nincs beállítva') : ''}
        />
      </td>
      <td className="px-4 py-2.5 align-top">
        <button
          className="btn-primary"
          disabled={save.isPending || (setting.secret && !value)}
          onClick={() => save.mutate({ value })}
        >
          <Save className="h-4 w-4" /> {save.isPending ? 'Mentés…' : 'Mentés'}
        </button>
        {save.isError && (
          <p className="mt-1 flex items-center gap-1 text-[11px] text-rose-600">
            <AlertTriangle className="h-3 w-3" /> {String(save.error)}
          </p>
        )}
        {save.isSuccess && <p className="mt-1 text-[11px] text-emerald-600">Mentve.</p>}
      </td>
    </tr>
  );
}

export function SettingsPage(): JSX.Element {
  const settings = useQuery({
    queryKey: ['/api/admin/settings'],
    queryFn: () => api.get<SystemSetting[]>('/api/admin/settings'),
  });

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold text-heading">Settings</h2>
      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-4 py-2.5">Kulcs</th>
              <th className="px-4 py-2.5">Érték</th>
              <th className="w-32 px-4 py-2.5"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {settings.isLoading && (
              <tr><td colSpan={3} className="px-4 py-6 text-center text-slate-500">Betöltés…</td></tr>
            )}
            {settings.isError && (
              <tr><td colSpan={3} className="px-4 py-6 text-center text-rose-600">Hiba: {String(settings.error)}</td></tr>
            )}
            {settings.data?.length === 0 && (
              <tr><td colSpan={3} className="px-4 py-6 text-center text-slate-500">Nincs beállítás.</td></tr>
            )}
            {settings.data?.map((s) => <SettingRow key={s.key} setting={s} />)}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-500">
        A titkos (lakat ikonnal jelölt) értékek nem jelennek meg. Üresen hagyva a meglévő
        érték változatlan marad; új érték megadása felülírja.
      </p>
    </div>
  );
}
